import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { useEffect, useState } from 'react'
import { toast } from 'react-hot-toast'
import { useNavigate } from 'react-router'

import type { Organization, OrganizationDetail } from '@ankor-io/common/types/account'

import { useUserContext } from '@/providers/user/useUserContext'
import { Dialog } from '@/ui-components/Dialog'

import OrganizationEditModalContent from './OrganizationEditModalContent'
import OrganizationListModalContent from './OrganizationListModalContent'

type OrganizationsModalProps = {
  isOpen: boolean
  onClose: () => void
}

const OrganizationsModal = ({ isOpen, onClose }: OrganizationsModalProps) => {
  const navigate = useNavigate()
  const { getAccessToken } = useKindeAuth()
  const { organizations, activeOrganizationId, updateActiveOrganizationId, updateOrganization } = useUserContext()

  const [organizationDetail, setOrganization] = useState<OrganizationDetail | null>(
    activeOrganizationId ? organizations[activeOrganizationId] || null : null,
  )
  const [isEditing, setIsEditing] = useState(false)
  const [isEditSaving, setIsEditSaving] = useState(false)

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  const handleSwitchOrganization = (orgId: string) => {
    updateActiveOrganizationId(orgId)
    setOrganization(organizations[orgId] || null)
    onClose()
    navigate(`/`)
  }

  const handleEditStart = (org: OrganizationDetail) => {
    setOrganization(org)
    setIsEditing(true)
    // TODO: integrate editing of the organization
    // setEditForm({ name: org.name, logo: org.logo || '' })
  }

  const handleEditSave = async () => {
    if (!organizationDetail) {
      toast.error('Something went wrong. No organization found.')
      return
    }

    try {
      const updatedOrganization: Organization = {
        id: organizationDetail.id,
        name: organizationDetail.name,
        description: organizationDetail.description,
      }

      setIsEditSaving(true)
      const res = await fetch(`/api/iam/org/${updatedOrganization.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await getAccessToken()}`,
        },
        body: JSON.stringify(updatedOrganization),
      })

      if (res.ok) {
        toast.success('Organization name saved')
        updateOrganization(organizationDetail.id, organizationDetail.name, organizationDetail.description)
        setIsEditing(false)
      } else {
        toast.error('Failed to save organization name')
      }
    } catch (error) {
      console.error(error)
    }

    // TODO: update the organization in the user context

    setIsEditSaving(false)
    onClose()
  }

  const handleEditCancel = () => {
    setOrganization(null)
    setIsEditing(false)
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleEditCancel}
    >
      {isEditing && organizationDetail ? (
        <OrganizationEditModalContent
          organization={organizationDetail}
          setOrganization={setOrganization}
          onSave={handleEditSave}
          onCancel={handleEditCancel}
          isEditSaving={isEditSaving}
        />
      ) : (
        <OrganizationListModalContent
          organizations={organizations}
          activeOrganizationId={activeOrganizationId}
          onClose={onClose}
          onSwitchOrganization={handleSwitchOrganization}
          onEditStart={handleEditStart}
        />
      )}
    </Dialog>
  )
}

export default OrganizationsModal
